import type { Dispatch, SetStateAction } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle } from "@/ui/components/ui/dialog";
import { Badge } from "@/ui/components/ui/badge";
import type { AlertImage } from "@/alerts/types/machine-alert";
import { labelConfidencePercent } from "@/shared/utils/confidence";

interface AlertImageLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: AlertImage[];
  /** Shared with the card's carousel so open/close stays on the same image. */
  index: number;
  onIndexChange: Dispatch<SetStateAction<number>>;
  /** `image_id` of the alert frame (highest-confidence detection), or null. */
  alertFrameId: number | null;
  alt: string;
}

/**
 * Fullscreen image viewer for the alert detail card. Reuses the shadcn `Dialog`
 * (Radix) primitive so backdrop dimming, the portal, focus trapping, scroll lock,
 * Esc-to-close and click-outside-to-close come for free — clicking the image
 * itself stays inside the content, so it does not dismiss. Paging is driven by
 * the on-screen arrow buttons only (no window arrow-key listener — that would
 * fight `useMapShortcuts`); the shared `index` keeps it in sync with the card.
 */
export const AlertImageLightbox = ({
  open,
  onOpenChange,
  images,
  index,
  onIndexChange,
  alertFrameId,
  alt,
}: AlertImageLightboxProps) => {
  const { t } = useTranslation("map");
  const safeIndex = images.length > 0 ? Math.min(index, images.length - 1) : 0;
  const currentImage = images[safeIndex] ?? null;
  if (!currentImage) return null;

  const isAlertFrame = currentImage.image_id === alertFrameId;
  const confidence = labelConfidencePercent(currentImage.labels);
  const hasMultiple = images.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        aria-describedby={undefined}
        className="max-w-none w-auto border-0 bg-transparent p-0 shadow-none gap-0"
      >
        <DialogTitle className="sr-only">{t("lightbox.title")}</DialogTitle>

        <div className="relative flex items-center justify-center">
          <img
            src={currentImage.signed_url}
            alt={alt}
            className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
          />

          {isAlertFrame && (
            <Badge className="pointer-events-none absolute left-3 top-3 h-6 px-2 text-xs bg-amber-500 text-white border-transparent">
              {t("lightbox.alertFrame")}
            </Badge>
          )}

          {confidence != null && (
            <div className="pointer-events-none absolute bottom-3 left-3 rounded bg-black/60 px-2 py-1 text-xs text-white">
              {t("lightbox.confidence", { value: confidence })}
            </div>
          )}

          <button
            type="button"
            aria-label={t("lightbox.close")}
            onClick={() => onOpenChange(false)}
            className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
          >
            <X className="h-5 w-5" />
          </button>

          {hasMultiple && (
            <>
              <button
                type="button"
                aria-label={t("lightbox.previousImage")}
                onClick={() => onIndexChange((i) => (i <= 0 ? images.length - 1 : i - 1))}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                aria-label={t("lightbox.nextImage")}
                onClick={() => onIndexChange((i) => (i >= images.length - 1 ? 0 : i + 1))}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="pointer-events-none absolute bottom-3 right-3 rounded bg-black/60 px-2 py-1 text-xs text-white">
                {safeIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AlertImageLightbox;
